import { Button } from "@sellar-io/components";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useEvents } from "../../../hooks";
import { useOrderDispatch } from "../../../hooks/api/v2/order/v2/useOrderDispatch";
import { OrderPaymentDueFormFragment } from "./OrderPaymentDueFormFragment";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";

type OrderDispatchedFormValues = {
  paymentDue: string | null;
};

export function OrderDispatchedDialog({
  active,
  onDismiss,
  orderId,
  retailerId,
  deliveryPlannedAt,
  invoiceSent,
  invoicePaid,
  isInvoiceCreationStage,
  paymentType,
}: {
  active: boolean;
  onDismiss: () => void;
  retailerId: number;
  orderId: number;
  deliveryPlannedAt: Date | null;
  invoiceSent: boolean;
  invoicePaid: boolean;
  isInvoiceCreationStage: boolean;
  paymentType: OrderGetResponseType["paymentType"];
}): ReactElement {
  const showSellarCreditFeatures = paymentType === "sellarCredit";
  const { track } = useEvents();

  const form = useForm<OrderDispatchedFormValues>({
    shouldUseNativeValidation: true,
  });

  const orderDispatchMutation = useOrderDispatch(orderId);

  const showPaymentDueInput =
    !invoiceSent &&
    !invoicePaid &&
    isInvoiceCreationStage &&
    !showSellarCreditFeatures;

  const onDispatch: SubmitHandler<OrderDispatchedFormValues> = async (data) => {
    // oxlint-disable-next-line react/purity -- Captured when this deferred submit handler runs.
    const dispatchedAt = new Date();
    const updatedPaymentDue = data.paymentDue;

    orderDispatchMutation
      .mutateAsync({
        dispatchedAt,
        ...(showPaymentDueInput &&
          updatedPaymentDue && {
            paymentDue: new Date(updatedPaymentDue),
          }),
      })
      .then(() => {
        track("Order Marked As Dispatched", {
          initiator: "supplier",
          orderId: orderId,
          dispatchedAt,
        });
        onDismiss();
      });
  };

  const description = deliveryPlannedAt
    ? `Completing this action will mark the order as dispatched, with an estimated arrival date of ${dayjs(
        deliveryPlannedAt,
      ).format("YYYY-MM-DD")}. This cannot be undone.`
    : `Completing this action will mark the order as dispatched. This cannot be undone.`;

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <form onSubmit={form.handleSubmit(onDispatch)}>
        <DialogNavigation
          title={"Mark this order as dispatched"}
          description={description}
          onClickLeftIcon={() => onDismiss()}
          leftIcon={"CHEVRON_DOWN"}
        />
        <DialogContent>
          {showPaymentDueInput && (
            <OrderPaymentDueFormFragment retailerId={retailerId} form={form} />
          )}
        </DialogContent>
        <DialogCTA>
          <Button type="submit" loading={orderDispatchMutation.isPending}>
            Order dispatch
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
}
